import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  AlertTriangle, 
  Navigation, 
  Clock, 
  CheckCircle2, 
  Search,
  MapPin,
  Phone,
  Truck,
  Wrench,
  RefreshCw,
  ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Status renkleri ve etiketleri
const STATUS_CONFIG = {
  new: { label: 'Yeni', color: 'bg-red-500', textColor: 'text-white' },
  on_the_way: { label: 'Yoldayım', color: 'bg-blue-500', textColor: 'text-white' },
  investigating: { label: 'İnceleniyor', color: 'bg-yellow-500', textColor: 'text-black' },
  assigned: { label: 'Atandı', color: 'bg-purple-500', textColor: 'text-white' },
  in_repair: { label: 'Onarımda', color: 'bg-orange-500', textColor: 'text-white' },
  resolved: { label: 'Çözüldü', color: 'bg-green-500', textColor: 'text-white' },
  closed: { label: 'Kapatıldı', color: 'bg-gray-500', textColor: 'text-white' }
};

const PRIORITY_CONFIG = {
  critical: { label: 'Kritik', color: 'bg-red-600' },
  high: { label: 'Yüksek', color: 'bg-orange-500' },
  medium: { label: 'Orta', color: 'bg-yellow-500' },
  low: { label: 'Düşük', color: 'bg-green-500' }
};

const YardimciDashboardPage = () => {
  const { user, isSimulationActive, getEffectiveRole, getEffectiveSiteId } = useAuth();
  const [faults, setFaults] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    new: 0,
    on_the_way: 0,
    investigating: 0,
    resolved: 0
  });
  
  // Canlı izleme verileri
  const [liveData, setLiveData] = useState({
    online_stations: 0,
    total_stations: 0,
    active_charging: 0,
    total_power_kw: 0,
    monthly_energy_kwh: 0,
    co2_saved_kg: 0,
    live_stations: []
  });
  
  // Simülasyon kontrolü
  const simulationActive = isSimulationActive ? isSimulationActive() : false;
  const effectiveRole = getEffectiveRole ? getEffectiveRole() : user?.role;
  const effectiveSiteId = getEffectiveSiteId ? getEffectiveSiteId() : user?.site_id;
  
  // Hoş geldiniz mesajı için isim belirleme
  const getDisplayName = () => {
    if (simulationActive) {
      return 'Yardımcı (Simülasyon)';
    }
    return user?.full_name || user?.email || 'Yardımcı';
  };

  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem('hims_token');
      const headers = { 
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      // Canlı izleme verilerini getir
      try {
        let liveUrl = `${API_URL}/api/dashboard/live`;
        if (effectiveSiteId) {
          liveUrl += `?site_id=${effectiveSiteId}`;
        }
        const liveRes = await fetch(liveUrl, { headers });
        if (liveRes.ok) {
          const live = await liveRes.json();
          setLiveData(live);
        }
      } catch (liveErr) {
        console.error('Live data fetch error:', liveErr);
      }

      // Lokasyonları getir
      try {
        let locUrl = `${API_URL}/api/locations`;
        // Simülasyon modunda site filtresi
        if (effectiveSiteId) {
          locUrl += `?account_id=${effectiveSiteId}`;
        }
        const locRes = await fetch(locUrl, { headers });
        if (locRes.ok) {
          let locData = await locRes.json();
          locData = Array.isArray(locData) ? locData : [];
          // Ekstra frontend filtresi
          if (effectiveSiteId) {
            locData = locData.filter(loc => loc.account_id === effectiveSiteId || loc.site_id === effectiveSiteId);
          }
          setLocations(locData);
        } else {
          setLocations([]);
        }
      } catch (locErr) {
        console.error('Locations fetch error:', locErr);
        setLocations([]);
      }

      // Arızaları getir
      try {
        let faultUrl = `${API_URL}/api/faults?include_closed=false`;
        // Simülasyon modunda site filtresi
        if (effectiveSiteId) {
          faultUrl += `&site_id=${effectiveSiteId}`;
        }
        const faultRes = await fetch(faultUrl, { headers });
        if (faultRes.ok) {
          const faultData = await faultRes.json();
          let faultList = Array.isArray(faultData?.faults) ? faultData.faults : (Array.isArray(faultData) ? faultData : []);
          
          // Ekstra frontend filtresi
          if (effectiveSiteId) {
            faultList = faultList.filter(f => f.site_id === effectiveSiteId || f.account_id === effectiveSiteId);
          }
          
          setFaults(faultList);
          
          // İstatistikleri hesapla
          setStats({
            total: faultList.length,
            new: faultList.filter(f => f.status === 'new').length,
            on_the_way: faultList.filter(f => f.status === 'on_the_way').length,
            investigating: faultList.filter(f => f.status === 'investigating').length,
            resolved: faultList.filter(f => f.status === 'resolved').length
          });
        } else {
          setFaults([]);
          setStats({ total: 0, new: 0, on_the_way: 0, investigating: 0, resolved: 0 });
        }
      } catch (faultErr) {
        console.error('Faults fetch error:', faultErr);
        setFaults([]);
        setStats({ total: 0, new: 0, on_the_way: 0, investigating: 0, resolved: 0 });
      }
    } catch (error) {
      console.error('Data fetch error:', error);
      // Hata durumunda toast gösterme - simülasyon modunda normal olabilir
    } finally {
      setLoading(false);
    }
  }, [effectiveSiteId]);

  useEffect(() => {
    fetchData();
    // Her 30 saniyede bir yenile
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleQuickStatus = async (faultId, newStatus) => {
    setUpdating(faultId);
    try {
      const token = localStorage.getItem('hims_token');
      const res = await fetch(
        `${API_URL}/api/faults/${faultId}/quick-status?status=${newStatus}`,
        {
          method: 'POST',
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (res.ok) {
        toast.success(`Durum güncellendi: ${STATUS_CONFIG[newStatus]?.label || newStatus}`);
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Güncelleme başarısız');
      }
    } catch (error) {
      console.error('Status update error:', error);
      toast.error('Bağlantı hatası');
    } finally {
      setUpdating(null);
    }
  };

  const openGoogleMaps = (lat, lng) => {
    if (lat && lng) {
      window.open(`https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`, '_blank');
    } else {
      toast.error('Konum bilgisi mevcut değil');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold">Yardımcı Paneli</h1>
          <p className="text-muted-foreground">
            Hoş geldiniz, {getDisplayName()}
          </p>
        </div>
        <Button onClick={fetchData} variant="outline" size="sm">
          <RefreshCw className="h-4 w-4 mr-2" />
          Yenile
        </Button>
      </div>

      {/* Canlı İzleme Kartları */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full ${liveData?.online_stations > 0 ? 'bg-emerald-500 animate-pulse' : 'bg-slate-400'}`} />
            Canlı İzleme
            <span className="text-sm font-normal text-muted-foreground ml-2">
              {liveData?.online_stations || 0} cihaz çevrimiçi
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="p-4 rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800">
              <p className="text-sm text-blue-600 dark:text-blue-400">Bağlı Cihaz</p>
              <p className="text-2xl font-bold text-blue-700 dark:text-blue-300">
                {liveData?.online_stations || 0}/{liveData?.total_stations || 0}
              </p>
            </div>
            <div className="p-4 rounded-xl bg-purple-50 dark:bg-purple-900/20 border border-purple-100 dark:border-purple-800">
              <p className="text-sm text-purple-600 dark:text-purple-400">Aktif Şarj</p>
              <p className="text-2xl font-bold text-purple-700 dark:text-purple-300">
                {liveData?.active_charging || 0}
              </p>
            </div>
            <div className="p-4 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-800">
              <p className="text-sm text-amber-600 dark:text-amber-400">Anlık Güç</p>
              <p className="text-2xl font-bold text-amber-700 dark:text-amber-300">
                {(liveData?.total_power_kw || 0).toFixed(1)} kW
              </p>
            </div>
            <div className="p-4 rounded-xl bg-cyan-50 dark:bg-cyan-900/20 border border-cyan-100 dark:border-cyan-800">
              <p className="text-sm text-cyan-600 dark:text-cyan-400">Aylık Tüketim</p>
              <p className="text-2xl font-bold text-cyan-700 dark:text-cyan-300">
                {(liveData?.monthly_energy_kwh || 0).toFixed(1)} kWh
              </p>
            </div>
            <div className="p-4 rounded-xl bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-100 dark:border-emerald-800">
              <p className="text-sm text-emerald-600 dark:text-emerald-400">CO₂ Tasarrufu</p>
              <p className="text-2xl font-bold text-emerald-700 dark:text-emerald-300">
                {(liveData?.co2_saved_kg || 0).toFixed(1)} kg
              </p>
              <p className="text-xs text-emerald-500 dark:text-emerald-400">Bu ay</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* İstasyon Durumu */}
      {liveData?.live_stations?.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">İstasyon Durumu</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {liveData.live_stations.map((station, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                  <div className="flex items-center gap-3">
                    <div className={`w-2 h-2 rounded-full ${station.is_online ? 'bg-emerald-500' : 'bg-slate-400'}`} />
                    <div>
                      <p className="font-medium">{station.station_name}</p>
                      <p className="text-sm text-muted-foreground">{station.charger_id}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className={`text-sm font-medium ${station.is_online ? 'text-emerald-600' : 'text-slate-500'}`}>
                      {station.is_online ? (station.is_charging ? 'Şarj Ediyor' : 'Çevrimiçi') : 'Çevrimdışı'}
                    </p>
                    {station.is_charging && (
                      <p className="text-xs text-muted-foreground">{station.current_power_kw} kW</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Arıza Özeti - Küçük kartlar */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">Arıza Özeti</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-5 gap-2">
            <div className="p-3 rounded-lg bg-slate-100 dark:bg-slate-800 text-center">
              <p className="text-xs text-slate-500 dark:text-slate-400">Toplam</p>
              <p className="text-xl font-bold">{stats.total}</p>
            </div>
            <div className="p-3 rounded-lg bg-red-100 dark:bg-red-900/30 text-center">
              <p className="text-xs text-red-600 dark:text-red-400">Yeni</p>
              <p className="text-xl font-bold text-red-600 dark:text-red-400">{stats.new}</p>
            </div>
            <div className="p-3 rounded-lg bg-blue-100 dark:bg-blue-900/30 text-center">
              <p className="text-xs text-blue-600 dark:text-blue-400">Yolda</p>
              <p className="text-xl font-bold text-blue-600 dark:text-blue-400">{stats.on_the_way}</p>
            </div>
            <div className="p-3 rounded-lg bg-yellow-100 dark:bg-yellow-900/30 text-center">
              <p className="text-xs text-yellow-600 dark:text-yellow-500">İnceleniyor</p>
              <p className="text-xl font-bold text-yellow-600 dark:text-yellow-500">{stats.investigating}</p>
            </div>
            <div className="p-3 rounded-lg bg-green-100 dark:bg-green-900/30 text-center">
              <p className="text-xs text-green-600 dark:text-green-400">Çözüldü</p>
              <p className="text-xl font-bold text-green-600 dark:text-green-400">{stats.resolved}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Atanmış Lokasyonlar */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <MapPin className="h-5 w-5 text-primary" />
            Atanmış Lokasyonlarım
          </CardTitle>
        </CardHeader>
        <CardContent>
          {locations.length === 0 ? (
            <p className="text-muted-foreground text-center py-4">
              Henüz atanmış lokasyon yok
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {locations.map((loc) => (
                <div 
                  key={loc.id} 
                  className="p-3 rounded-lg bg-accent/30 border border-border"
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium">{loc.name}</p>
                      <p className="text-sm text-muted-foreground capitalize">
                        {loc.type === 'bina' ? 'Bina' : loc.type === 'otopark' ? 'Otopark' : 'Açık Alan'}
                      </p>
                    </div>
                    <Badge variant="outline">{loc.charger_count || 0} cihaz</Badge>
                  </div>
                  {loc.description && (
                    <p className="text-xs text-muted-foreground mt-2 line-clamp-2">
                      {loc.description}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Arıza Listesi */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Wrench className="h-5 w-5 text-orange-500" />
            Açık Arızalar
          </CardTitle>
        </CardHeader>
        <CardContent>
          {faults.length === 0 ? (
            <div className="text-center py-8">
              <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-3" />
              <p className="text-muted-foreground">Açık arıza bulunmuyor</p>
            </div>
          ) : (
            <div className="space-y-4">
              {faults.map((fault) => (
                <div 
                  key={fault.id}
                  className="p-4 rounded-lg bg-card border border-border hover:border-primary/50 transition-colors"
                  data-testid={`fault-card-${fault.id}`}
                >
                  {/* Üst Satır - Başlık ve Öncelik */}
                  <div className="flex flex-wrap justify-between items-start gap-2 mb-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold truncate">{fault.title}</h3>
                        <Badge className={`${PRIORITY_CONFIG[fault.priority]?.color} text-white text-xs`}>
                          {PRIORITY_CONFIG[fault.priority]?.label || fault.priority}
                        </Badge>
                        <Badge className={`${STATUS_CONFIG[fault.status]?.color} ${STATUS_CONFIG[fault.status]?.textColor} text-xs`}>
                          {STATUS_CONFIG[fault.status]?.label || fault.status}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">
                        {fault.station_name} • {fault.location_name || fault.site_name}
                      </p>
                    </div>
                    <span className="text-xs text-muted-foreground whitespace-nowrap">
                      {formatDate(fault.created_at)}
                    </span>
                  </div>

                  {/* Açıklama */}
                  {fault.description && (
                    <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                      {fault.description}
                    </p>
                  )}

                  {/* Timeline (son 3 giriş) */}
                  {fault.timeline && fault.timeline.length > 0 && (
                    <div className="mb-3 p-2 rounded bg-accent/30">
                      <p className="text-xs text-muted-foreground mb-1">Son durumlar:</p>
                      <div className="space-y-1">
                        {fault.timeline.slice(-3).reverse().map((entry, idx) => (
                          <div key={idx} className="flex items-center gap-2 text-xs">
                            <Clock className="h-3 w-3 text-muted-foreground" />
                            <span className="text-muted-foreground">
                              {new Date(entry.timestamp).toLocaleString('tr-TR', {
                                day: '2-digit',
                                month: '2-digit',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </span>
                            <span>-</span>
                            <span>{entry.note}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Aksiyon Butonları */}
                  <div className="flex flex-wrap gap-2">
                    {/* Yoldayım butonu - sadece yeni veya atanmış durumda */}
                    {['new', 'assigned'].includes(fault.status) && (
                      <Button
                        size="sm"
                        variant="default"
                        className="bg-blue-600 hover:bg-blue-700"
                        onClick={() => handleQuickStatus(fault.id, 'on_the_way')}
                        disabled={updating === fault.id}
                        data-testid={`btn-on-the-way-${fault.id}`}
                      >
                        {updating === fault.id ? (
                          <RefreshCw className="h-4 w-4 animate-spin mr-1" />
                        ) : (
                          <Truck className="h-4 w-4 mr-1" />
                        )}
                        Yoldayım
                      </Button>
                    )}

                    {/* İnceleniyor butonu - yolda durumunda */}
                    {fault.status === 'on_the_way' && (
                      <Button
                        size="sm"
                        variant="default"
                        className="bg-yellow-600 hover:bg-yellow-700"
                        onClick={() => handleQuickStatus(fault.id, 'investigating')}
                        disabled={updating === fault.id}
                        data-testid={`btn-investigating-${fault.id}`}
                      >
                        {updating === fault.id ? (
                          <RefreshCw className="h-4 w-4 animate-spin mr-1" />
                        ) : (
                          <Search className="h-4 w-4 mr-1" />
                        )}
                        İnceleniyor
                      </Button>
                    )}

                    {/* Çözüldü butonu - inceleniyor veya onarımda */}
                    {['investigating', 'in_repair', 'on_the_way'].includes(fault.status) && (
                      <Button
                        size="sm"
                        variant="default"
                        className="bg-green-600 hover:bg-green-700"
                        onClick={() => handleQuickStatus(fault.id, 'resolved')}
                        disabled={updating === fault.id}
                        data-testid={`btn-resolved-${fault.id}`}
                      >
                        {updating === fault.id ? (
                          <RefreshCw className="h-4 w-4 animate-spin mr-1" />
                        ) : (
                          <CheckCircle2 className="h-4 w-4 mr-1" />
                        )}
                        Çözüldü
                      </Button>
                    )}

                    {/* Konuma Git butonu */}
                    {(fault.latitude && fault.longitude) && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openGoogleMaps(fault.latitude, fault.longitude)}
                        data-testid={`btn-navigate-${fault.id}`}
                      >
                        <Navigation className="h-4 w-4 mr-1" />
                        Konuma Git
                      </Button>
                    )}

                    {/* Yardımcı Telefonu */}
                    {fault.assigned_to_phone && (
                      <a href={`tel:${fault.assigned_to_phone}`}>
                        <Button size="sm" variant="outline">
                          <Phone className="h-4 w-4 mr-1" />
                          Ara
                        </Button>
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default YardimciDashboardPage;
